type RollingNumberProps = {
  /** The (non-negative integer) value to display. */
  value: number;
  /** Roll duration in milliseconds. */
  durationMs?: number;
};

/**
 * Odometer-style number. Each decimal place is a vertical 0–9 strip that slides
 * to its target digit, so a changing value rolls (like YouTube's view /
 * subscriber counter) instead of snapping. Tabular figures keep every digit the
 * same width, so the surrounding layout stays stable and measurable.
 *
 * The plain numeric value is exposed to assistive tech via `aria-label`; the
 * rolling strips themselves are decorative (`aria-hidden`).
 */
export function RollingNumber({ value, durationMs = 350 }: RollingNumberProps) {
  const digits = Math.max(0, Math.trunc(value)).toString().split("");
  return (
    // `vertical-align: middle` leaves the digit strip sitting ~0.1em below the
    // optical center of the adjacent text; a small em-relative lift re-centers
    // it (scales with font size, so it holds wherever the odometer is used).
    <span
      className="relative inline-flex align-middle tabular-nums"
      style={{ top: "-0.1em" }}
    >
      {/* The rolling strips below are aria-hidden (each renders all ten digits),
          so expose the actual value to assistive tech here. */}
      <span className="sr-only">{value}</span>
      {digits.map((digit, i) => (
        <RollingDigit
          // Key by place (distance from the right) so each column persists
          // across value changes — it animates to the new digit instead of
          // remounting. A new most-significant column (e.g. 9 → 10) mounts
          // already showing its digit.
          key={digits.length - 1 - i}
          digit={Number(digit)}
          durationMs={durationMs}
        />
      ))}
    </span>
  );
}

function RollingDigit({
  digit,
  durationMs,
}: {
  digit: number;
  durationMs: number;
}) {
  return (
    <span
      aria-hidden
      className="relative inline-block h-[1em] overflow-hidden leading-none"
    >
      <span
        className="flex flex-col"
        style={{
          // The strip is 10 rows tall (10em); shifting up by `digit * 10%` of
          // its own height brings the target digit into the 1em window.
          transform: `translateY(${-digit * 10}%)`,
          transition: `transform ${durationMs}ms cubic-bezier(0.22, 1, 0.36, 1)`,
        }}
      >
        {Array.from({ length: 10 }, (_, n) => (
          <span
            key={n}
            className="flex h-[1em] items-center justify-center leading-none"
          >
            {n}
          </span>
        ))}
      </span>
    </span>
  );
}
